import { Link, router } from '@inertiajs/react'
import type { OnboardingChecklist, SurveyListItem } from '@/types'
import { useAccountPath } from '@/lib/useAccountPath'

interface GettingStartedChecklistProps {
  checklist: OnboardingChecklist
  surveys: SurveyListItem[]
}

interface StepDef {
  key: keyof Omit<OnboardingChecklist, 'dismissed'>
  label: string
  hint: string
  cta: string
  href: string | null
}

export default function GettingStartedChecklist({ checklist, surveys }: GettingStartedChecklistProps) {
  const ap = useAccountPath()

  // Link targets derive from the live survey list: publish points at the first
  // real draft, first-response at the first live survey's share card.
  const firstDraft = surveys.find((s) => !s.demo && s.status === 'draft')
  const firstActive = surveys.find((s) => !s.demo && s.status === 'active')

  const steps: StepDef[] = [
    {
      key: 'created',
      label: 'Create your first survey',
      hint: 'Pick a template — it takes about 3 minutes.',
      cta: 'New survey',
      href: ap('/surveys/new'),
    },
    {
      key: 'branding',
      label: 'Add your branding',
      hint: 'Your logo and colours appear on surveys and reports.',
      cta: 'Branding settings',
      href: ap('/settings'),
    },
    {
      key: 'published',
      label: 'Publish your survey',
      hint: 'Publishing generates the shareable link.',
      cta: 'Open survey',
      href: firstDraft ? ap(`/surveys/${firstDraft.id}`) : ap('/surveys/new'),
    },
    {
      key: 'first_response',
      label: 'Get your first response',
      hint: 'Share the link or QR code with participants.',
      cta: 'Share survey',
      href: firstActive ? ap(`/surveys/${firstActive.id}`) : null,
    },
  ]

  const doneCount = steps.filter((s) => checklist[s.key]).length
  const allDone = doneCount === steps.length
  const nextStep = steps.find((s) => !checklist[s.key])

  function handleDismiss() {
    router.post(ap('/onboarding/dismiss_checklist'))
  }

  if (allDone) {
    return (
      <div role="alert" className="alert alert-soft alert-success text-sm">
        <span className="iconify lucide--circle-check-big size-5" />
        <div>
          <span className="font-medium">You're all set!</span>{' '}
          Survey published, first response in — you're collecting impact data.
        </div>
        <button onClick={handleDismiss} className="btn btn-ghost btn-sm">
          Dismiss
        </button>
      </div>
    )
  }

  return (
    <div className="card bg-base-100">
      <div className="card-body space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">Getting started</h2>
            <span className="text-sm text-base-content/50 tabular-nums">
              {doneCount} of {steps.length}
            </span>
          </div>
          <button
            onClick={handleDismiss}
            className="btn btn-square btn-ghost btn-sm"
            title="Hide this checklist"
          >
            <span className="iconify lucide--x size-4" />
          </button>
        </div>

        <progress
          className="progress progress-primary w-full"
          value={doneCount}
          max={steps.length}
        />

        <ul className="space-y-3">
          {steps.map((step) => {
            const done = checklist[step.key]
            const isNext = nextStep?.key === step.key
            return (
              <li key={step.key} className="flex items-start gap-3">
                <span
                  className={`iconify size-5 mt-0.5 shrink-0 ${
                    done
                      ? 'lucide--circle-check-big text-success'
                      : 'lucide--circle text-base-content/25'
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <span className={done ? 'text-base-content/50 line-through' : 'font-medium'}>
                    {step.label}
                  </span>
                  {!done && (
                    <p className="text-xs text-base-content/50">{step.hint}</p>
                  )}
                </div>
                {isNext && step.href && (
                  <Link href={step.href} className="btn btn-primary btn-sm shrink-0">
                    {step.cta}
                  </Link>
                )}
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
